import { AxiosError } from "axios";
import { SharedState } from "@/common/state/index";
import { UseToast, ToastType } from "@/components/root/toast/toast.state";
import { AppAnalytics } from "@/analytics";
import { getQueryParams } from "@/utils/url";
import { PartnerIdStorage } from "@/storage";

// Paths whose failures the caller reports itself, so the generic fallback
// toast below would double up. `/reports/storage` 404s when a report has no
// PowerPoint version — an expected condition that download_report surfaces
// with its own message, not a server error.
const abort500Toast = ["/extproxy/summary", "/reports/storage"];

const shouldAbort500Toast = (url: string) =>
  abort500Toast.some((path) => url.search(path) > -1);

export const getErrStatusCode = (e: AxiosError): number => {
  console.log("getErrStatusCode e : ", e);
  if (!e || !e.response) {
    return 0;
  }
  return e.response?.status;
};

const error_body = (e: AxiosError): any | null => {
  if (e && e.response && e.response.data) {
    return e.response.data;
  } else {
    return null;
  }
};

export const apiErrHandler = (e: AxiosError) => {
  const code = getErrStatusCode(e);
  if (e && e.config.baseURL) {
    const [path] = e.config.baseURL.split("/").reverse();
    if (path === "analytic") {
      AppAnalytics.stop();
      return;
    }
  }

  if (
    e &&
    e.config &&
    e.config.headers &&
    e.config.headers.shouldRetry == "true"
  ) {
    return;
  }
  // Opt-out by URL for optional lookups that handle their own errors: the
  // risk-story / effective-trust aggregation calls 404 (or fail) for events not
  // supported by the new APIs, and the caller falls back to the legacy UI
  // silently. Suppress the global toast for these without adding a custom
  // request header (a custom header would force a CORS preflight that some of
  // these endpoints reject).
  const reqUrl = (e && e.config && e.config.url) || "";
  if (
    reqUrl.indexOf("event-risk-stories") !== -1 ||
    reqUrl.indexOf("effective-trust-familiarity-aggregations") !== -1
  ) {
    return code;
  }
  const body = error_body(e);
  switch (true) {
    case code === 0:
      if (SharedState.env.value.env == "uat") return;
      UseToast().show({
        type: ToastType.OFFLINE,
        msg: "Looks like you're offline",
      });
      break;
    case code === 400:
      const resObj = e.response?.data;
      if (
        resObj &&
        resObj.errors &&
        resObj.errors[0] === "Context creation failed: authorization error"
      ) {
        SharedState.logout();
      }
      if (body && body.reason) {
        UseToast().show({
          type: ToastType.ALERT,
          msg: body.reason,
        });
      }
      break;
    case code === 401:
      const p = getQueryParams();
      if (p.partnerId) {
        PartnerIdStorage.save(p.partnerId);
      }
      if (body && body.rejectedUser) {
        SharedState.logout({ email: body.rejectedUser });
      } else {
        SharedState.logout();
      }
      break;
    case code === 403:
      if (SharedState.env.value.env == "uat") return;
      UseToast().show({
        type: ToastType.ALERT,
        msg: "You are not allowed to preform this action",
      });
      break;
    case code === 424:
      break;
    case code == 409:
      console.log("409 error , should be handled by caller fn");
      break;
    case code >= 500:
    default:
      if (SharedState.env.value.env == "uat") return;
      const url = e.config.url || "";
      if (shouldAbort500Toast(url)) {
        // abort
        console.log("aborting 500 toast");
      } else {
        UseToast().show({
          type: ToastType.ALERT,
          msg: "server error",
        });
      }

      break;
  }
  return code;
};
